// Application bootstrap
import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import App from "./App.tsx";
import "./index.css";
import { ErrorBoundary } from "./components/ErrorBoundary";

// Auto-reload bei Chunk-/Modul-Ladefehlern (typisch nach Deploys mit gehashten Bundles).
// Diese Fehler werden oft NICHT von der React ErrorBoundary gefangen (Promise-Rejections
// aus dynamischen Imports), deshalb global behandeln.
const CHUNK_ERROR_PATTERNS = [
  'Importing a module script failed',
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'Unable to preload CSS',
  'ChunkLoadError',
];

function isChunkError(msg: string | undefined | null): boolean {
  if (!msg) return false;
  return CHUNK_ERROR_PATTERNS.some((p) => msg.includes(p));
}

function triggerCacheBustReload() {
  try {
    const key = 'chunkReloadAt';
    const last = parseInt(sessionStorage.getItem(key) || '0', 10);
    const now = Date.now();
    // Max ein Reload pro 10s, max 2 in 60s
    if (now - last < 10_000) return;
    const countKey = 'chunkReloadCount';
    const count = parseInt(sessionStorage.getItem(countKey) || '0', 10);
    const recent = now - last < 60_000 ? count : 0;
    if (recent >= 2) return;
    sessionStorage.setItem(key, String(now));
    sessionStorage.setItem(countKey, String(recent + 1));
    const url = new URL(window.location.href);
    url.searchParams.set('_cb', String(now));
    window.location.replace(url.toString());
  } catch {
    window.location.reload();
  }
}

window.addEventListener('vite:preloadError', (e) => {
  e.preventDefault();
  triggerCacheBustReload();
});

window.addEventListener('error', (e) => {
  if (isChunkError(e.message)) {
    e.preventDefault();
    triggerCacheBustReload();
  }
});

window.addEventListener('unhandledrejection', (e) => {
  const msg = (e.reason && (e.reason.message || String(e.reason))) || '';
  if (isChunkError(msg)) {
    e.preventDefault();
    triggerCacheBustReload();
  }
});

createRoot(document.getElementById("root")!).render(
  <ErrorBoundary>
    <HelmetProvider>
      <App />
    </HelmetProvider>
  </ErrorBoundary>
);
